import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Container, Typography, Paper, CircularProgress } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useAuthenticated } from '../hooks/useAuthenticated';
import { useLazyQuery } from '@apollo/client';
import { GET_ME } from '../graphql/getMe';

const Success = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isPlanActive } = useAuthenticated();
  const [loading, setLoading] = useState(true);
  const [planActivated, setPlanActivated] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [getMe] = useLazyQuery(GET_ME, {
    fetchPolicy: 'network-only', // Bypass cache
  });

  const checkPaymentStatus = async (sessionId: string) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/api/check-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ sessionId }),
      });

      if (response.ok) {
        const result = await response.json();
        if (result.planUpdated) {
          // Refresh user data after plan update
          await getMe();
          setPlanActivated(true);
          return true;
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
    return false;
  };
  
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    
    if (sessionId) {
      // Initial call to refresh user data
      getMe().then(async (result) => {
        if (result.data?.getMe?.email) {
          setUserEmail(result.data.getMe.email);
        }
        if (result.data?.getMe?.planExpiresAt) {
          setPlanActivated(true);
        } else {
          // Try to check payment status manually
          await checkPaymentStatus(sessionId);
        }
        
        // Poll for plan activation (webhook might take a moment)
        const pollInterval = setInterval(async () => {
          try {
            const pollResult = await getMe();
            if (pollResult.data?.getMe?.planExpiresAt) {
              setPlanActivated(true);
              clearInterval(pollInterval);
            } else {
              // Try manual check again
              await checkPaymentStatus(sessionId);
            }
          } catch (error) {
            console.error('Error polling for plan update:', error);
          }
        }, 2000); // Poll every 2 seconds

        // Stop polling after 30 seconds
        setTimeout(() => {
          clearInterval(pollInterval);
          setLoading(false);
        }, 30000);

        // Show success after initial delay
        setTimeout(() => {
          setLoading(false);
        }, 2000);
      }).catch((error) => {
        console.error('Error refreshing user data:', error);
        setLoading(false);
      });
    } else {
      setLoading(false);
    }
  }, [searchParams, getMe]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '111.11vh',
          background: 'linear-gradient(to bottom, rgb(227, 248, 255), rgb(120, 162, 240))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ mb: 2, color: '#2c1a7e' }} />
          <Typography variant="h5" sx={{ color: '#2c1a7e' }}>
            Processing your payment...
          </Typography>
          <Typography variant="body1" sx={{ color: '#455a64', mt: 1 }}>
            This may take a couple of minutes...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '111.11vh',
        background: 'linear-gradient(to bottom, rgb(227, 248, 255), rgb(120, 162, 240))',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={2}
          sx={{
            p: 5,
            borderRadius: '16px',
            textAlign: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <CheckCircle
            sx={{
              fontSize: 50,
              color: '#4caf50',
              mb: 3,
              opacity: 0.9,
            }}
          />
          <Typography
            variant="h4"
            component="h1"
            sx={{
              fontWeight: 500,
              color: '#2c1a7e',
              mb: 2,
              letterSpacing: '0.5px',
            }}
          >
            Payment Successful!
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: '#455a64',
              fontWeight: 300,
              lineHeight: 1.6,
            }}
          >
            {planActivated 
              ? 'Thank you for your purchase. Your plan is now active.'
              : 'Thank you for your purchase. Your plan will be activated shortly.'
            }
          </Typography>
          <Typography
            variant="body1"
            sx={{
              mb: 3,
              color: '#2c1a7e',
              fontWeight: 500,
              opacity: 0.8,
            }}
          >
            A confirmation email has been sent to {userEmail || 'your inbox'}.
          </Typography>
          {!planActivated && (
            <Typography
              variant="body2"
              sx={{
                mb: 3,
                color: '#666',
                fontStyle: 'italic',
                fontWeight: 300,
                fontSize: '0.875rem',
              }}
            >
              If your plan doesn't activate within a few minutes, please contact support.
            </Typography>
          )}
          <Button
            variant="contained"
            size="medium"
            onClick={handleGoToDashboard}
            sx={{
              borderRadius: '24px',
              py: 1.2,
              px: 3,
              fontSize: '0.95rem',
              textTransform: 'none',
              fontWeight: 500,
              boxShadow: '0 2px 8px rgba(44, 26, 126, 0.2)',
              '&:hover': {
                boxShadow: '0 4px 12px rgba(44, 26, 126, 0.3)',
              },
            }}
          >
            Go to Dashboard
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default Success; 